import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import useLoginStore from "@/stores/loginStore";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { TextLink } from "@/components/text";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/button";
import { Input, InputGroup } from "@/components/input";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { PaginationComponent } from "@/components/PaginationComponent";
import { Badge } from "@/components/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import {
  useQueryGetGroupList,
  type IGroupListParams,
} from "@/apis/groups/getGroupList";
import { getPaginationInfo } from "@/types/pagination";

const GroupListPage: React.FC = () => {
  const { clearToken } = useLoginStore();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useState<IGroupListParams>({
    page: 1,
    limit: 10,
  });

  // API 호출
  const { data, isLoading, error } = useQueryGetGroupList(searchParams);

  // 검색 처리
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams((prev) => ({
      ...prev,
      name: searchTerm,
      page: 1, // 검색 시 첫 페이지로 이동
    }));
  };

  // 페이지네이션 정보
  const paginationInfo = data ? getPaginationInfo(data) : null;

  // 페이지 이동 핸들러
  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  // 그룹 추가 페이지로 이동
  const handleAddGroup = () => {
    navigate("/groups/create");
  };

  // 그룹 상세 페이지로 이동
  const handleViewGroup = (groupId: string) => {
    navigate(`/groups/${groupId}`);
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <Heading>Group List</Heading>
        <Text className="text-zinc-500">
          Manage all groups for your academy. You can create new groups,
          organize students, and track progress.
        </Text>

        <div className="flex justify-between mt-4">
          <Button onClick={handleAddGroup}>
            <PlusIcon />
            Add Group
          </Button>

          <form onSubmit={handleSearch} className="flex items-center">
            <InputGroup>
              <MagnifyingGlassIcon />
              <Input
                name="search"
                placeholder="Search by name..."
                aria-label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button type="submit" className="ml-2">
              Search
            </Button>
          </form>
        </div>

        {isLoading ? (
          <div className="text-center py-8">Loading groups...</div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">
            Error occurred: {(error as Error).toString()}
          </div>
        ) : data?.items.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">No groups found.</div>
        ) : (
          <Table className="mt-4">
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Members</TableHeader>
                <TableHeader>Created</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.items.map((group) => (
                <TableRow
                  key={group.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleViewGroup(group.id)}
                >
                  <TableCell>
                    <div className="font-medium">{group.name}</div>
                  </TableCell>
                  <TableCell>
                    {group.status === "active" ? (
                      <Badge color="lime">Active</Badge>
                    ) : (
                      <Badge color="zinc">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-zinc-500">
                    {group.user_count} members
                  </TableCell>
                  <TableCell className="text-zinc-500">
                    {new Date(group.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {paginationInfo && (
          <div className="mt-8 flex justify-center">
            <PaginationComponent
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              hasNextPage={paginationInfo.hasNextPage}
              hasPrevPage={paginationInfo.hasPrevPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default GroupListPage;
